import { useEffect, useMemo, useState } from 'react';
import { CalendarDaysIcon, InformationCircleIcon, TrophyIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/features/auth/hooks';
import { useRoleSwitcher } from '@/features/auth/hooks/useRoleSwitcher';
import { useSchool } from '@/contexts/SchoolContext';
import CampaignNominationModal from './CampaignNominationModal';
import CampaignActionSheet from './CampaignActionSheet';
import {
  getCampaignActions,
  isCampaignLive,
  subscribeActiveCampaigns,
} from '../services/campaignService';

const toneClasses = {
  info: 'border-info/30 bg-info/10',
  success: 'border-success/30 bg-success/10',
  warning: 'border-warning/40 bg-warning/10',
};

export default function CampaignBanner() {
  const { user } = useAuth();
  const { currentRole } = useRoleSwitcher();
  const { schoolId } = useSchool();
  const [campaigns, setCampaigns] = useState([]);
  const [now, setNow] = useState(() => new Date());
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [detailsCampaign, setDetailsCampaign] = useState(null);
  const [submittedStudent, setSubmittedStudent] = useState(null);

  useEffect(() => (
    subscribeActiveCampaigns(
      schoolId,
      setCampaigns,
      error => console.error('[CampaignBanner] Subscription failed:', error)
    )
  ), [schoolId]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const visibleCampaigns = useMemo(() => (
    campaigns.filter(campaign => (
      isCampaignLive(campaign, now) &&
      (
        !campaign.audienceRoles?.length ||
        campaign.audienceRoles.includes(currentRole)
      )
    ))
  ), [campaigns, currentRole, now]);

  if (!visibleCampaigns.length) return null;

  return (
    <>
      <section className="w-full bg-base-200/30 px-4 py-2" aria-label="Active school campaigns">
        <div className="mx-auto max-w-7xl space-y-2">
          {visibleCampaigns.map(campaign => {
            const hasWinner = Boolean(campaign.winner?.studentName);
            const actions = getCampaignActions(campaign);
            const primaryAction = actions.find(action => action.id === campaign.primaryActionId) || actions[0];
            const hasSliceTwoActions = Boolean(campaign.actions?.length);
            const artwork = campaign.banner?.imageUrl;
            const positionX = campaign.banner?.positionX ?? 50;
            const positionY = campaign.banner?.positionY ?? 50;
            const zoom = Math.max(100, campaign.banner?.zoom ?? 100);
            return (
              <article
                key={campaign.id}
                className={`relative isolate min-h-28 overflow-hidden rounded-lg ${artwork ? 'border-0 bg-transparent text-neutral-content' : `border p-4 ${toneClasses[campaign.banner?.tone] || toneClasses.success}`}`}
              >
                {artwork && (
                  <>
                    <img
                      src={artwork}
                      alt={campaign.banner?.imageAlt || ''}
                      className="absolute inset-0 -z-20 h-full w-full object-cover"
                      style={{
                        objectPosition: `${positionX}% ${positionY}%`,
                        transform: `scale(${zoom / 100})`,
                      }}
                    />
                  </>
                )}
                <div className={`flex min-h-28 flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between ${artwork ? 'sm:p-5' : 'p-0'}`}>
                  <div className="flex min-w-0 gap-3">
                    <TrophyIcon className={`mt-0.5 h-5 w-5 shrink-0 ${artwork ? 'text-warning' : 'text-warning'}`} />
                    <div className="min-w-0">
                      <h2 className={`${artwork ? 'text-base sm:text-lg' : 'text-sm'} font-semibold`}>
                        {campaign.banner?.headline || campaign.title}
                      </h2>
                      <p className={`text-sm ${artwork ? 'max-w-2xl text-white/90' : 'text-base-content/70'}`}>
                        {hasWinner
                          ? campaign.banner?.resultMessage ||
                            `${campaign.winner.studentName} has been selected as the winner.`
                          : campaign.banner?.message}
                      </p>
                      {campaign.banner?.dateLabel && (
                        <p className={`mt-1 flex items-center gap-1.5 text-xs font-semibold ${artwork ? 'text-white' : 'text-base-content/75'}`}>
                          <CalendarDaysIcon className="h-4 w-4" />
                          {campaign.banner.dateLabel}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-wrap gap-2">
                    {campaign.banner?.details && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setDetailsCampaign(campaign)}
                      >
                        <InformationCircleIcon className="h-4 w-4" />
                        {campaign.banner.learnMoreLabel || 'Learn more'}
                      </button>
                    )}
                    {(!hasWinner || hasSliceTwoActions) && primaryAction && (
                      <button
                        type="button"
                        className={`btn btn-sm ${campaign.banner?.details ? 'btn-outline border-white/60 text-white hover:border-white hover:bg-white hover:text-neutral' : 'btn-primary'}`}
                        onClick={() => {
                          setSubmittedStudent(null);
                          setSelectedCampaign(campaign);
                        }}
                      >
                        {primaryAction.label || campaign.banner?.actionLabel || 'Open campaign'}
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {detailsCampaign && (
        <div className="modal modal-open" role="dialog" aria-modal="true" aria-labelledby="campaign-information-title">
          <div className="modal-box max-w-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">Campaign information</p>
                <h2 id="campaign-information-title" className="mt-1 text-2xl font-bold">
                  {detailsCampaign.banner?.headline || detailsCampaign.title}
                </h2>
                {detailsCampaign.banner?.dateLabel && (
                  <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-base-content/70">
                    <CalendarDaysIcon className="h-5 w-5" />
                    {detailsCampaign.banner.dateLabel}
                  </p>
                )}
              </div>
              <button type="button" className="btn btn-ghost btn-sm btn-circle" onClick={() => setDetailsCampaign(null)} aria-label="Close campaign information">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-5 whitespace-pre-line text-sm leading-6 text-base-content/80">
              {detailsCampaign.banner?.details}
            </p>
            <div className="modal-action">
              <button type="button" className="btn btn-primary" onClick={() => setDetailsCampaign(null)}>Close</button>
            </div>
          </div>
          <button type="button" className="modal-backdrop" onClick={() => setDetailsCampaign(null)} aria-label="Close campaign information backdrop" />
        </div>
      )}

      {selectedCampaign?.actions?.length ? (
        <CampaignActionSheet
          campaign={selectedCampaign}
          schoolId={schoolId}
          user={user}
          currentRole={currentRole}
          onClose={() => setSelectedCampaign(null)}
        />
      ) : selectedCampaign && !submittedStudent && (
        <CampaignNominationModal
          campaign={selectedCampaign}
          schoolId={schoolId}
          user={user}
          onClose={() => setSelectedCampaign(null)}
          onSubmitted={setSubmittedStudent}
        />
      )}

      {selectedCampaign && submittedStudent && (
        <div className="modal modal-open" role="dialog" aria-modal="true" aria-labelledby="nomination-complete-title">
          <div className="modal-box max-w-md text-center">
            <TrophyIcon className="mx-auto h-10 w-10 text-warning" />
            <h2 id="nomination-complete-title" className="mt-3 text-xl font-bold">
              Nomination submitted
            </h2>
            <p className="mt-2 text-base-content/65">
              {submittedStudent.firstName} {submittedStudent.surname || submittedStudent.lastName} is now entered in {selectedCampaign.title}.
            </p>
            <div className="modal-action justify-center">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setSelectedCampaign(null);
                  setSubmittedStudent(null);
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
